/* eslint-disable jsx-a11y/tabindex-no-positive */
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, A11y, Virtual } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { useConfigStore } from '@/stores';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import type { PoiContentSlide } from '@/types/shop';
import ShopCarouselItem from '@/components/feature/shop/shop-carousel/shop-carousel-item';
import ShopFnbFacilityCarouselCount from '@/components/common/shop-fnb-facility/carousel-count';
import ShopFnbFacilitySlideNavigationButton from '@/components/common/shop-fnb-facility/buttons/slide-navigation';
import { useAccessibilityTTS, useOrientation, useTickSoundInteraction, useVirtualizedCarousel } from '@/hooks';
import { CarouselType, getCarouselConfig } from '@/constants/carousel';
import { useRef, useCallback, useMemo, useEffect } from 'react';
import { useGlobalCountdownReset } from '@/hooks/useGlobalCountdownReset';
import paginationStyles from '@/components/feature/shop/shop-carousel/shop-carousel-pagination.module.scss';

interface VirtualizedShopCarouselProps {
    contents: PoiContentSlide[];
    className?: string;
    onClickPoiContent?: (data: PoiContentSlide) => void;
}

const VirtualizedShopCarousel = ({ contents, className = '', onClickPoiContent }: VirtualizedShopCarouselProps) => {
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const { speak } = useAccessibilityTTS();
    const voice = useConfigStore((state) => state.config.tts.voice);
    const { isHorizontal } = useOrientation();
    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);

    const { t } = useTranslation();
    const isZoomIn = useConfigStore((state) => state.config.ui.zoomIn);
    const isZoomClosed = useConfigStore((state) => state.config.ui.zoomClosed);
    const isZoomed = useMemo(() => {
        return isZoomIn || (isZoomClosed === false && isZoomIn === false);
    }, [isZoomIn, isZoomClosed]);
    const config = getCarouselConfig(CarouselType.SHOP);
    const { resetCountdown } = useGlobalCountdownReset();
    const { playTick } = useTickSoundInteraction();

    const { swiperRef, prevButtonRef, nextButtonRef, state, computed, virtualSlidesData, navigation, getVirtualSlideProps } = useVirtualizedCarousel({
        contents,
        config,
        dependencies: [],
    });

    const nextExecutingRef = useRef(false);
    const prevExecutingRef = useRef(false);

    const prevButtonText = t('floor_guide.text_to_speech.back');
    const nextButtonText = t('floor_guide.text_to_speech.next');

    const inactiveButtonText = t('common.button.tts.inactive_button');
    const activeButtonText = t('common.button.tts.button');

    const handlePrevWithTTS = useCallback(() => {
        resetCountdown();
        if (prevExecutingRef.current) {
            return;
        }

        prevExecutingRef.current = true;
        if (isZoomed && swiperRef.current) {
            swiperRef.current.allowSlideNext = true;
            swiperRef.current.allowSlidePrev = true;
        }

        navigation.handlePrevClick();

        if (isZoomed && swiperRef.current) {
            setTimeout(() => {
                if (swiperRef.current) {
                    swiperRef.current.allowSlideNext = false;
                    swiperRef.current.allowSlidePrev = false;
                }
            }, 50);
        }

        if (state.isBeginning) {
            if (voice) {
                speak(t('shop.slide.prev_btn_disabled'));
            }
        } else {
            if (voice) {
                speak(t('shop.slide.prev_btn_pressed', { page: state.currentPage - 1 }));
            }
        }

        setTimeout(() => {
            prevExecutingRef.current = false;
        }, 100);
    }, [navigation, state.isBeginning, state.currentPage, voice, speak, t, isZoomed, resetCountdown, swiperRef]);

    const handleNextWithTTS = useCallback(() => {
        resetCountdown();
        if (nextExecutingRef.current) {
            return;
        }

        nextExecutingRef.current = true;

        if (isZoomed && swiperRef.current) {
            swiperRef.current.allowSlideNext = true;
            swiperRef.current.allowSlidePrev = true;
        }

        navigation.handleNextClick();

        if (isZoomed && swiperRef.current) {
            setTimeout(() => {
                if (swiperRef.current) {
                    swiperRef.current.allowSlideNext = false;
                    swiperRef.current.allowSlidePrev = false;
                }
            }, 50);
        }

        if (state.isEnd) {
            if (voice) {
                speak(t('shop.slide.next_btn_disabled'));
            }
        } else {
            if (voice) {
                speak(t('shop.slide.next_btn_pressed', { page: state.currentPage + 1 }));
            }
        }

        setTimeout(() => {
            nextExecutingRef.current = false;
        }, 100);
    }, [navigation, state.isEnd, state.currentPage, voice, speak, t, isZoomed, resetCountdown, swiperRef]);

    useEffect(() => {
        if (swiperRef.current) {
            swiperRef.current.allowTouchMove = true;

            if (isZoomed) {
                swiperRef.current.allowSlideNext = false;
                swiperRef.current.allowSlidePrev = false;
            } else {
                swiperRef.current.allowSlideNext = true;
                swiperRef.current.allowSlidePrev = true;
            }
        }
    }, [isZoomed, swiperRef]);

    if (contents.length === 0) {
        return null;
    }

    return (
        <div className={clsx('flex h-full w-full flex-col items-center', className)}>
            <div
                className={clsx(
                    `relative flex h-full w-full flex-col items-center ${isHorizontal ? 'pt-[calc(24*var(--vh-unit))]' : screenMode === 'split' ? '' : 'pt-[calc(60*var(--vh-unit))]'}`
                )}
            >
                <div className="relative flex w-full flex-col items-center">
                    <div className={clsx(paginationStyles.carouselFrame)} style={{ width: '100%' }}>
                        <div className="flex w-full justify-center">
                            <Swiper
                                modules={[Navigation, A11y, Virtual]}
                                allowSlideNext={!isZoomed}
                                allowSlidePrev={!isZoomed}
                                allowTouchMove={true}
                                spaceBetween={2}
                                slidesPerView={1}
                                virtual={{
                                    enabled: true,
                                    cache: false,
                                }}
                                onSwiper={(swiper) => {
                                    swiperRef.current = swiper;
                                }}
                                onSlideChange={navigation.handleSlideChange}
                                className="brand-carousel-swiper"
                                threshold={isZoomed ? 1000 : 5}
                                resistance={true}
                                resistanceRatio={isZoomed ? 1 : 0.85}
                                shortSwipes={!isZoomed}
                                longSwipes={!isZoomed}
                                followFinger={true}
                                role="region"
                                aria-label={t('shop.slide.carousel')}
                                keyboard={{
                                    enabled: false,
                                }}
                            >
                                {virtualSlidesData.map((slideData) => {
                                    const slideProps = getVirtualSlideProps(slideData);
                                    if (!slideProps) return null;

                                    return (
                                        <SwiperSlide key={slideData.slideIndex} virtualIndex={slideData.slideIndex}>
                                            <div
                                                key={slideProps.slideIndex}
                                                className={slideProps.className}
                                                style={{
                                                    ...slideProps.gridStyle,
                                                    overflow: isZoomed ? 'auto' : 'visible',
                                                    pointerEvents: 'auto',
                                                    touchAction: isZoomed ? 'pan-x pan-y' : 'auto',
                                                }}
                                                data-region-nav={slideProps.isInView}
                                            >
                                                {slideProps.slideContents.map((item) => (
                                                    <ShopCarouselItem
                                                        key={item.itemIndex}
                                                        id={slideProps.isInView && item.itemIndex === 0 ? 'shop-first-content' : undefined}
                                                        tabIndex={item.tabIndex}
                                                        content={item.content}
                                                        onClick={onClickPoiContent}
                                                    />
                                                ))}
                                            </div>
                                        </SwiperSlide>
                                    );
                                })}
                            </Swiper>
                        </div>

                        <div className={clsx(paginationStyles.pagination, contrast && paginationStyles.paginationContrast, !isHorizontal && screenMode !== 'split' && paginationStyles['pagination-vertical'])}>
                            <ShopFnbFacilitySlideNavigationButton
                                data-region-nav
                                data-tts-scope="carousel-navigation-button"
                                navigationDirection="previous"
                                ref={prevButtonRef as React.RefObject<HTMLButtonElement>}
                                aria-label={t('shop.slide.prev_btn')}
                                isDisabled={state.isBeginning}
                                tabIndex={3}
                                role="button"
                                data-role={t('common.data_role.button')}
                                data-interactive
                                className={clsx(paginationStyles.navPrev, !state.isBeginning && paginationStyles.navPrevEnabled)}
                                onFocus={() => {
                                    if (state.isBeginning) {
                                        speak(`${prevButtonText} ${inactiveButtonText}`);
                                    } else {
                                        speak(`${prevButtonText} ${activeButtonText}`);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        playTick();
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handlePrevWithTTS();
                                    }
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handlePrevWithTTS();
                                }}
                                onClick={() => {
                                    prevButtonRef.current?.focus();
                                }}
                            />

                            <ShopFnbFacilityCarouselCount
                                id="shop-page-count"
                                variant="inline"
                                contrast={contrast}
                                page={state.currentPage}
                                totalPage={state.totalSlides}
                                tabIndex={4}
                            />

                            <ShopFnbFacilitySlideNavigationButton
                                data-region-nav
                                data-tts-scope="carousel-navigation-button"
                                navigationDirection="next"
                                ref={nextButtonRef as React.RefObject<HTMLButtonElement>}
                                aria-label={t('shop.slide.next_btn')}
                                isDisabled={state.isEnd}
                                tabIndex={5}
                                role="button"
                                data-role={t('common.data_role.button')}
                                data-interactive
                                className={clsx(paginationStyles.navNext, !state.isEnd && paginationStyles.navNextEnabled)}
                                onFocus={() => {
                                    if (state.isEnd) {
                                        speak(`${nextButtonText} ${inactiveButtonText}`);
                                    } else {
                                        speak(`${nextButtonText} ${activeButtonText}`);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        playTick();
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleNextWithTTS();
                                    }
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleNextWithTTS();
                                }}
                                onClick={() => {
                                    nextButtonRef.current?.focus();
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VirtualizedShopCarousel;
